import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn, LogOut, ChevronDown } from "lucide-react";
import axios from "axios";
import Logo from "@/components/Logo";
import AuthModal from "@/components/AuthModal";
import { clearTokens } from "@/authClient";
import { useI18n } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = ["fr", "es", "en"];

function LangSwitcher({ onSelect }) {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center gap-1 border border-pine/15 bg-paper px-1 py-1" data-testid="language-switcher">
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`lang-switch-${l}`}
          onClick={() => {
            setLang(l);
            onSelect?.();
          }}
          aria-pressed={lang === l}
          className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
            lang === l ? "bg-pine text-cream" : "text-sand hover:text-copper"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function Avatar({ user, size = "h-8 w-8" }) {
  if (user.picture) {
    return <img src={user.picture} alt={user.name} className={`${size} rounded-full object-cover ring-1 ring-copper/40`} referrerPolicy="no-referrer" />;
  }
  return (
    <span className={`flex ${size} items-center justify-center rounded-full bg-pine font-mono text-xs text-cream`}>
      {(user.name || user.email || "M")[0].toUpperCase()}
    </span>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [requests, setRequests] = useState(null);
  const panelRef = useRef(null);
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(r.data);
      } catch (e) {
        if (e.response?.status === 401) {
          try {
            await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(r.data);
            return;
          } catch {}
        }
        setUser(null);
      }
    };
    if (!window.location.hash?.includes("session_id=")) {
      checkAuth();
    }
    const onAuthChange = (e) => setUser(e.detail);
    window.addEventListener("my-auth-changed", onAuthChange);
    return () => window.removeEventListener("my-auth-changed", onAuthChange);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      clearTokens();
      setUser(null);
      setPanelOpen(false);
      setRequests(null);
      window.dispatchEvent(new CustomEvent("my-auth-changed", { detail: null }));
    }
  };

  const togglePanel = async () => {
    const next = !panelOpen;
    setPanelOpen(next);
    if (next && requests === null) {
      try {
        const r = await axios.get(`${API}/my/requests`, { withCredentials: true });
        setRequests(r.data);
      } catch {
        setRequests([]);
      }
    }
  };

  const LINKS = [
    { label: t.nav.varieties, href: "#varietes" },
    { label: t.nav.origin, href: "#origine" },
    { label: t.nav.b2b, href: "#coffee-shops" },
    { label: t.nav.process, href: "#processus" },
    { label: t.nav.contact, href: "#contact" },
  ];

  const AuthArea = () => {
    if (user === undefined) return null;
    if (user === null) {
      return (
        <button
          type="button"
          data-testid="google-login-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 border border-pine/25 bg-paper px-4 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-pine transition-all duration-300 hover:border-copper hover:text-copper"
        >
          <LogIn className="h-4 w-4" />
          {t.nav.login}
        </button>
      );
    }
    return (
      <div className="relative" ref={panelRef} data-testid="user-menu">
        <button
          type="button"
          data-testid="user-menu-toggle"
          onClick={togglePanel}
          className="flex items-center gap-2 border border-pine/15 bg-paper px-2 py-1.5 transition-colors duration-300 hover:border-copper/50"
        >
          <Avatar user={user} />
          <ChevronDown className={`h-3.5 w-3.5 text-sand transition-transform duration-300 ${panelOpen ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence>
          {panelOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              data-testid="account-panel"
              className="absolute right-0 top-full mt-2 w-80 border border-pine/15 bg-paper p-5 shadow-xl shadow-pine/10"
            >
              <div className="flex items-center gap-3 border-b border-pine/10 pb-4">
                <Avatar user={user} size="h-10 w-10" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-pine" data-testid="user-name">{user.name}</div>
                  <div className="truncate text-xs font-light text-sand">{user.email}</div>
                </div>
              </div>
              <div className="py-4">
                <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-copper">
                  {t.auth.myRequests}
                </div>
                {requests === null && <div className="text-xs text-sand">…</div>}
                {requests !== null && requests.length === 0 && (
                  <div className="text-xs font-light text-sand" data-testid="account-no-requests">{t.auth.noRequests}</div>
                )}
                {requests !== null && requests.length > 0 && (
                  <ul className="flex max-h-48 flex-col gap-2 overflow-y-auto" data-testid="account-requests-list">
                    {requests.map((r) => (
                      <li key={r.id} className="border border-pine/10 bg-cream px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-xs font-medium text-pine">{r.business}</span>
                          <span className="shrink-0 font-mono text-[9px] text-sand">
                            {new Date(r.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-sand">
                          {r.volume}{(r.varieties || []).length > 0 ? ` · ${r.varieties.join(", ")}` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {user.role === "admin" && (
                <a
                  href="/admin"
                  data-testid="admin-space-link"
                  className="mb-2 flex w-full items-center justify-center gap-2 border border-copper/30 bg-cream px-4 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-copper transition-colors duration-300 hover:bg-copper hover:text-paper"
                >
                  {t.admin.link}
                </a>
              )}
              <button
                type="button"
                data-testid="logout-button"
                onClick={logout}
                className="flex w-full items-center justify-center gap-2 border-t border-pine/10 pt-4 text-xs uppercase tracking-[0.15em] text-sand transition-colors hover:text-copper"
              >
                <LogOut className="h-4 w-4" />
                {t.nav.logout}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "border-b border-pine/10 bg-cream/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8">
          <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-3">
            <Logo className="h-11 w-11" />
            <span className="font-serif text-2xl tracking-wide text-pine">
              Monte <span className="italic text-copper">Yolli</span>
            </span>
          </a>

          <nav className="hidden items-center gap-6 lg:flex xl:gap-8" aria-label="Navigation principale">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="copper-underline whitespace-nowrap text-sm font-light tracking-wide text-bark/70 transition-colors hover:text-pine"
              >
                {l.label}
              </a>
            ))}
            <LangSwitcher />
            <a
              href="#contact"
              data-testid="nav-cta-sample"
              className="whitespace-nowrap bg-pine px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:bg-copper"
            >
              {t.nav.cta}
            </a>
            <AuthArea />
          </nav>

          <div className="flex items-center gap-3 lg:hidden">
            <LangSwitcher />
            <button
              data-testid="nav-mobile-toggle"
              className="text-pine"
              onClick={() => setOpen((v) => !v)}
              aria-label="Ouvrir le menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden border-b border-pine/10 bg-cream/95 backdrop-blur-xl lg:hidden"
            >
              <div className="flex flex-col gap-1 px-6 py-6">
                {LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                    onClick={() => setOpen(false)}
                    className="py-3 font-serif text-2xl text-pine transition-colors hover:text-copper"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href="#contact"
                  data-testid="nav-mobile-cta-sample"
                  onClick={() => setOpen(false)}
                  className="mt-4 bg-pine px-5 py-3 text-center text-xs font-medium uppercase tracking-[0.2em] text-cream"
                >
                  {t.nav.cta}
                </a>
                <div className="mt-4 border-t border-pine/10 pt-4">
                  {user === null && (
                    <button
                      type="button"
                      data-testid="google-login-button-mobile"
                      onClick={() => {
                        setOpen(false);
                        setModalOpen(true);
                      }}
                      className="inline-flex w-full items-center justify-center gap-2 border border-pine/25 bg-paper px-4 py-3 text-xs font-medium uppercase tracking-[0.15em] text-pine"
                    >
                      <LogIn className="h-4 w-4" />
                      {t.nav.login}
                    </button>
                  )}
                  {user && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar user={user} size="h-9 w-9" />
                        <span className="text-sm font-light text-pine" data-testid="user-name-mobile">{user.name}</span>
                      </div>
                      <button
                        type="button"
                        data-testid="logout-button-mobile"
                        onClick={logout}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-sand hover:text-copper"
                      >
                        <LogOut className="h-4 w-4" />
                        {t.nav.logout}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <AuthModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAuth={(u) => {
          setUser(u);
          window.dispatchEvent(new CustomEvent("my-auth-changed", { detail: u }));
        }}
      />
    </>
  );
}
